/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useContext, createContext, FC, useMemo, useRef } from 'react'
import { makeid, sleep } from '@/utils/global.utils'
import { insertAction } from '@/utils/realtime.utils'
import { sendAction } from '@/services/sensors.service'
import { getObjectsForSite, warnActionNotPassed, futureCommands } from '@/services/realtime.service'
import { getConstraints } from '@/services/constraints.service'
import { useParams } from 'react-router-dom'
import { useSite } from '../useSite'
import { NotFoundError } from '@/utils/error.utils'
import { getGroups } from '@/services/constraints.service'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { event } from '@/utils/ga.utils'
import {
    RealtimeQueryData,
    ParsedObject,
    RawConstraints,
    AverageValues,
    NotificationData,
    DataMeasurement,
    Sensor,
    ZonebjectBase,
    Group,
} from './types'
import { parseRealtimeObjects, getStateValuesAverages } from './parseObjects/parseObject'
import { camelCaseDeep } from '@/utils/camelCaseDeep'
import { verifyActionIsPassed } from '@/utils/realtime.utils'
import { useErrorCodeTranslations } from '@/hooks/useErrorCodeTranslations'
import { ErrorCodeTranslations } from '@/services/locales.service'

const REFETCH_INTERVAL_MS = 5 * 1000
const STATUS_FOR_UNREACHABLE = 400

// Merge object lists keeping the first occurrence of each objectId (pass newest data first).
const mergeObjectsByMostRecent = (...objectGroups: any[][]): any[] => {
    const merged = new Map<number, any>()

    objectGroups.forEach((group) => {
        group?.forEach((obj) => {
            if (!obj) {
                return
            }
            const rawId = obj.ObjectId ?? obj.objectId
            if (rawId === undefined || rawId === null) {
                return
            }
            const objectId = typeof rawId === 'string' ? Number(rawId) : rawId
            if (Number.isNaN(objectId)) {
                return
            }

            if (!merged.has(objectId)) {
                merged.set(objectId, obj)
            }
        })
    })

    return Array.from(merged.values())
}

type Modification = any

export enum ActionStatus {
    REGISTERED = 'registered',
    PENDING = 'pending',
    SUCCESS = 'success',
    ERROR = 'error',
}

export const useRealtime = () => {
    const context = useContext(RealtimeContext)

    if (!context) {
        throw new Error('useRealtime must be used within a RealtimeProvider')
    }
    return context
}

type ActionVerificationResults = {
    actionsPassed: unknown[]
    actionsFailed: unknown[]
}
export interface RealtimeContextValue extends Record<string, any> {
    parsedObjects: ParsedObject[]
    averageIndicators: AverageValues
    actionStatus: ActionStatus | null
    siteConnectionStatus: ActionStatus
    resetActionStatus: () => void
    activeTabIndex: number
    setActiveTabIndex: (newIndex: number) => void
    isLoading: boolean
    addModification: any
    saveModifications: any
    rawSensors: any[]
    notifications: NotificationData[]
    groups: Group[]
    errorCodeTranslations: ErrorCodeTranslations
}

const RealtimeContext = createContext<RealtimeContextValue>({
    parsedObjects: [],
    averageIndicators: [],
    actionStatus: null,
    siteConnectionStatus: ActionStatus.PENDING,
    resetActionStatus: () => {},
    activeTabIndex: 0,
    setActiveTabIndex: () => {},
    isLoading: false,
    addModification: undefined,
    saveModifications: undefined,
    rawSensors: [],
    notifications: [],
    groups: [],
    errorCodeTranslations: {},
})

interface RealtimeProviderProps {
    children: React.ReactNode
}

const PAGE_SIZE = 25

export const RealtimeProvider: FC<RealtimeProviderProps> = ({ children }) => {
    const { site, objectsQuery } = useSite()
    const { objectId } = useParams<{ objectId?: string }>()
    const navigate = useNavigate()
    const [confirmDialog, setConfirmDialog] = useState(false)
    const [isInitialized, setIsInitialized] = useState(false)
    const [hasMoreData, setHasMoreData] = useState(true)
    const [loadedData, setLoadedData]: any = useState(null)
    const [targetPage, setTargetPage] = useState(null)
    const [actionStatus, setActionStatus] = useState<ActionStatus | null>(null)
    const [siteConnectionStatus, setSiteConnectionStatus] = useState<ActionStatus>(ActionStatus.PENDING)
    const notifications = useMemo<NotificationData[]>(() => [], [])
    const [isFetching, setIsFetching] = useState(false)
    const [page, setPage] = useState(1)
    const [maxPage, setMaxPage] = useState(1)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [objectFetched, setObjectFetched] = useState(false)
    const resetActionStatus = () => {
        setActionStatus(null)
    }
    const [activeTabIndex, setActiveTabIndex] = useState(0)
    const previousValuesRef = useRef<Record<number, number>>({})
    const handleNotifications = (dataMeasurements: DataMeasurement[], sensors: Sensor[], objects: ZonebjectBase[]) => {
        dataMeasurements.forEach((measurement) => {
            const sensor = sensors.find(
                (el) => el?.sensorId === measurement.sensorId && (el?.dataTypeId === 226 || el?.dataTypeId === 228)
            )
            if (!sensor) return
            const object = objects.find((el) => el?.objectId === sensor?.objectId)
            if (!object) return
            const prevValue = previousValuesRef.current[measurement.sensorId]
            if (prevValue === undefined) {
                previousValuesRef.current[measurement.sensorId] = measurement.value
                return
            }
            const find = notifications.find((el) => el?.dataMeasurementId === measurement.dataMeasurementId)
            const lastN = notifications?.filter((el) => el?.sensorId === measurement.sensorId)
            const lasDateTime = lastN?.length > 0 ? lastN[lastN.length - 1]?.dateTime : null
            if (prevValue !== measurement.value && !find && (!lasDateTime || lasDateTime < measurement.dateTime)) {
                previousValuesRef.current[measurement.sensorId] = measurement.value
                notifications.push({ ...measurement, sensor, object })
            }
        })
    }
    if (!site) {
        throw new Error('siteHash is required')
    }
    const [modifications, setModifications] = useState<Modification[]>([])

    const groupsQuery = useQuery<Group[]>({
        queryKey: ['groups-for-site', site.hash],
        queryFn: async () => {
            const res = await getGroups(site.hash)
            return res?.groups
        },
        staleTime: 1000 * 60 * 10,
    })

    const objects = objectsQuery.data || []
    const object = objects.find((obj) => String(obj.objectId) === objectId)
    if (objectId && !objectsQuery.isLoading && !object) {
        throw new NotFoundError(`Object with id ${objectId} not found`)
    }
    const handleActionResults = async ({ actionsPassed, actionsFailed }: ActionVerificationResults) => {
        if (actionsFailed.length > 0) {
            setActionStatus(ActionStatus.ERROR)
            return warnActionNotPassed({
                siteName: site.name,
                actions: JSON.stringify(actionsFailed),
            })
        } else if (actionsPassed.length > 0) {
            setActionStatus(ActionStatus.SUCCESS)
        }
    }

    const safe = (arr: any) => arr ?? []

    const realtimeQuery = useQuery<RealtimeQueryData>({
        queryKey: ['realtime', site.hash],
        queryFn: async () => {
            let data = []
            if (isFetching) {
                await sleep(2000)
            }
            setObjectFetched(true)
            data = await getObjectsForSite({
                hash: site.hash,
                realtime: false,
                ignore_temperature: false,
                ignore_fan_speed: true,
            })
            data.dataMeasurements = data.dataMeasurements || []
            data.objects = data.objects || []
            data.objectPms = data.objectPms || []
            data.sensors = data.sensors || []
            setLoadedData(data)

            setHasMoreData(false)

            if (data.status === STATUS_FOR_UNREACHABLE) {
                setSiteConnectionStatus(ActionStatus.ERROR)
                throw new Error('Site is unreachable')
            }
            const verificationResult = verifyActionIsPassed(data.objects) as ActionVerificationResults
            const rawSensors = data.sensors
            handleActionResults(verificationResult)
            const realtimeData = { ...camelCaseDeep(data), rawSensors } as unknown as RealtimeQueryData
            handleNotifications(realtimeData.dataMeasurements, realtimeData.sensors, realtimeData.objects)
            setSiteConnectionStatus(ActionStatus.SUCCESS)
            if (!isInitialized) {
                setIsInitialized(true)
            }
            return realtimeData
        },
        refetchInterval: REFETCH_INTERVAL_MS,
        refetchOnWindowFocus: true,
    })
    const constraintsQuery = useQuery({
        queryKey: ['constraints', site.hash],
        queryFn: async () => getConstraints(site.hash),
    })
    const rawConstraints = constraintsQuery.data?.constraints as Record<number, RawConstraints> | undefined
    const parsedObjects = useMemo(
        () => (realtimeQuery.data ? parseRealtimeObjects(realtimeQuery.data, rawConstraints || {}, site.pilot) : []),
        [realtimeQuery.data, rawConstraints, site.pilot]
    )

    const averageIndicators = getStateValuesAverages(parsedObjects)
    const addModification = (actuatorId: number, value: any, title: any, sensors: any, dataTypeId?: number) => {
        const array: any[] = [...modifications]
        const exist = array.find((el) => el.id === actuatorId)
        if (exist) {
            for (let i = 0; i < array.length; i++) {
                if (array[i].id === actuatorId) {
                    array[i].value = value
                    array[i].title = title
                    array[i].dataTypeId = dataTypeId
                    break
                }
            }
        } else {
            array.push({
                id: actuatorId,
                value,
                title,
                actuatorId: actuatorId,
                sensors,
                dataTypeId,
                createdAt: new Date(),
                hash: makeid(),
                objectName: objects.find((obj) => String(obj.objectId) === objectId)?.name,
                objectId: objectId,
                SetTemp: value > 2 ? value : null,
                Drive: value === 0 || value === 1 ? value : null,
            })
        }
        setModifications(array)
    }
    const clearModifications = () => {
        setModifications([])
    }

    const sendActionMutation = useMutation({
        mutationFn: async (hash: string | null) => {
            // Only navigate back if we're on an object telecommand page (objectId exists)
            // If objectId is undefined, we're on the site telecommand page and should stay
            if (objectId) {
                navigate(-1)
            }
            if (modifications && modifications?.length > 0) {
                const send = insertAction(modifications)
                if (send) {
                    sendAction({
                        groupHashes: hash ? [hash] : [],
                        modifications,
                        hash: site.hash,
                        fromDashboard: true,
                        selectedObject: { Name: 'TODO' },
                    })
                }
            }
            // setSendLoader(false)
            event('SendAction', `${site.name} - ${objectId}`)
            if (objectId) {
                setActionStatus(ActionStatus.PENDING)
            } else {
                setActionStatus(ActionStatus.REGISTERED)
                // Switch back to zones tab after saving site telecommand
                setActiveTabIndex(0)
            }
        },
        onMutate: () => {
            setActionStatus(ActionStatus.PENDING)
        },
        onSettled: () => {
            realtimeQuery.refetch()
        },
    })

    const saveModifications = async (hash: string | null = null, schedule?: { date: string; time: string }) => {
        // If schedule is provided, send future command instead
        if (schedule && schedule.date && schedule.time) {
            if (modifications && modifications.length > 0) {
                console.log('modifications', modifications)
                const commands = modifications.map((mod: any) => {
                    const isState = mod.value === 0 || mod.value === 1
                    return {
                        ObjectId: mod.objectId,
                        Time: schedule.time,
                        Date: schedule.date,
                        State: isState ? mod.value : null,
                        Setpoint: isState ? null : mod.value,
                    }
                })

                try {
                    const result = await futureCommands({
                        hash: site.hash,
                        commands,
                    })

                    if (result) {
                        setActionStatus(ActionStatus.SUCCESS)
                        clearModifications()
                        if (objectId) {
                            navigate(-1)
                        } else {
                            setActiveTabIndex(0)
                        }
                    } else {
                        setActionStatus(ActionStatus.ERROR)
                    }
                } catch (error) {
                    console.error('Error scheduling future command:', error)
                    setActionStatus(ActionStatus.ERROR)
                }
            }
            return
        }

        // Original save logic
        if (object?.objectTypeId === 1) {
            // setConfirmDialog(true)
            sendActionMutation.mutate(null)
        } else {
            sendActionMutation.mutate(hash)
        }
        if (object?.objectId) {
            const find = loadedData?.objects.find((obj: any) => obj.ObjectId === object?.objectId)

            if (find && find?.page) {
                setTimeout(() => {
                    setTargetPage(find?.page)
                }, 10000) // wait for the action to be sent
            }
        }
    }

    const { errorCodeTranslations } = useErrorCodeTranslations()

    return (
        <RealtimeContext.Provider
            value={{
                activeTabIndex,
                setActiveTabIndex,
                actionStatus,
                resetActionStatus,
                parsedObjects,
                averageIndicators,
                addModification,
                clearModifications,
                saveModifications,
                modifications,
                site,
                isLoading: objectsQuery.isLoading || realtimeQuery.isLoading,
                siteConnectionStatus,
                object,
                confirmDialog,
                setConfirmDialog,
                groups: groupsQuery.data || [],
                rawSensors: realtimeQuery.data?.rawSensors || [],
                notifications,
                errorCodeTranslations,
                hasMoreData,
                setIsFetching,
            }}
        >
            {children}
        </RealtimeContext.Provider>
    )
}
